<template>
  <div>
    <v-btn
      v-if="shouldShowChatButton"
      class="chatButton"
      color="#5E00EB"
      fab
      large
      dark
      @click="chatIsOpen = !chatIsOpen"
    >
      <v-icon class="chatIcon" v-if="!chatIsOpen">mdi-chat</v-icon>
      <v-icon v-else>mdi-menu-down</v-icon>
    </v-btn>
    <Transition name="fade">
      <div v-show="chatIsOpen" ref="chatContainer" class="chatContainer chatfade">
        <v-card
          class="rounded-lg"
          color="#5E00EB"
          :width="isMobileSized ? `${windowWidth}px` : '400'"
          height="700"
        >
          <v-card-text class="logoAndHistory">
            <img
              class="adacadoLogo"
              src="https://storage.googleapis.com/adacado-logos/adacadoLogo.svg"
            />
            <div class="chatHistoryButtons">
              <template v-if="hasChatHistory && !isViewingChatHistory && currentView === 'chat'">
                <div class="iconsAndHistoryText" @click="viewChatHistory">
                  <v-icon>mdi-chat-outline</v-icon>
                  <v-icon>mdi-menu-right</v-icon>
                </div>
                <div @click="viewChatHistory">View Chat History</div>
              </template>
              <div class="optionsText"
                ><span @click="toggleReportForm(currentView === 'bugs' ? 'chat' : 'bugs')">{{
                  currentView === 'bugs' ? 'Ask Question' : 'Report a Bug'
                }}</span>
                /
                <span @click="toggleReportForm(currentView === 'feedback' ? 'chat' : 'feedback')">{{
                  currentView === 'feedback' ? 'Ask Question' : 'Give Feedback'
                }}</span></div
              >
            </div>
          </v-card-text>
          <template v-if="currentView === 'bugs' || currentView === 'feedback'">
            <v-card-text
              v-if="!formDataSent"
              ref="formContainer"
              class="formContainer pa-0"
              :style="{ height: isMobileSized ? '83%' : '87%' }"
            >
              <div class="mb-2 reportTitle white--text font-weight-bold">{{
                currentView === 'bugs' ? 'Bug Report' : 'Give us feedback!'
              }}</div>
              <v-textarea
                v-model="textAreaInput"
                :rows="isMobileSized ? 11 : 17"
                :placeholder="
                  currentView === 'bugs' ? 'Describe the bug here' : feedbackPlaceholder
                "
                class="chatInputTextarea"
                no-resize
                solo
                flat
              ></v-textarea>
              <div
                class="d-flex sendReportButton white--text font-weight-bold"
                :class="textAreaInput.length ? '' : 'disabled'"
                @click="sendReport()"
              >
                Send
                <v-icon dark right :disabled="!canSendMessage" class="chatInputTextareaSend"
                  >mdi-send</v-icon
                >
              </div>
            </v-card-text>
            <v-card-text
              v-else-if="isSendingMessage"
              ref="formContainer"
              class="formContainer pa-0"
            >
              <div class="mb-2 reportTitle white--text font-weight-bold"
                >Sending {{ currentView === 'bugs' ? 'Bug Report' : 'Feeback' }}...</div
              >
              <div class="loadingSpinnerContainer mt-5" style="height: 100px;">
                <v-progress-circular indeterminate color="white" :size="50"></v-progress-circular>
              </div>
            </v-card-text>
            <v-card-text v-else-if="formDataSent">
              <div class="mb-2 white--text font-weight-bold reportTitle">{{ reportTitle }}</div>
              <div class="white--text">{{ reportMessage }}</div>
              <div class="d-flex justify-center">
                <v-btn dark color="#ff9f21" class="mt-10" @click="resetView">Ask a Question</v-btn>
              </div>
            </v-card-text>
          </template>
          <v-card-text
            v-if="currentView === 'chat'"
            ref="messageAreaContainer"
            class="messageAreaContainer"
            :class="isTextAreaFocused ? 'textAreaFocused' : ''"
          >
            <div class="messageArea">
              <template v-if="isViewingChatHistory">
                <div v-if="isLoadingChatHistory" class="loadingSpinnerContainer">
                  <v-progress-circular
                    indeterminate
                    color="#5E00EB"
                    :size="50"
                  ></v-progress-circular>
                </div>
                <template v-else>
                  <div
                    v-for="(thread, index) in chatHistory"
                    class="py-1 threadPreview"
                    :key="'thread' + index"
                    @click="loadSelectedThread(thread.threadId, index, true)"
                  >
                    <v-row class="threadPreviewRow">
                      <v-col cols="10">
                        <div class="threadText">{{ thread.text }}</div>
                        <div class="threadAge">{{ getLocalAge(thread.endTimestamp) }}</div>
                      </v-col>
                      <v-col cols="2">
                        <v-icon color="#5E00EB">mdi-menu-right</v-icon>
                      </v-col>
                    </v-row>
                    <!-- add a divider if it's not the last item in the list -->
                    <v-divider v-if="index < chatHistory.length - 1"></v-divider>
                  </div>
                </template>
              </template>
              <template v-else>
                <!-- loop through messages and display them here -->
                <div
                  v-for="(message, index) in messages"
                  class="py-1 chatMessage"
                  :class="{ user: message.isUser }"
                  :key="'chatMessage' + index"
                >
                  <div v-if="!message.isUser" class="avatarIcon"
                    ><v-icon>mdi-robot-outline</v-icon></div
                  >
                  <div
                    class="messageTextBox"
                    :class="message.isUser ? 'userMessage' : 'botMessage'"
                  >
                    <div class="whiteSpace" v-html="message.text"></div>
                    <div v-if="message.navigationLink">
                      <a @click="setLocalStorageAndRedirect(message.navigationLink)"
                        >Go to the relevant page</a
                      >
                    </div>
                  </div>
                  <div v-if="message.isUser" class="avatarIcon"
                    ><v-icon color="#5E00EB">mdi-emoticon-happy-outline</v-icon></div
                  >
                </div>
                <template v-if="chatbotIsTyping">
                  <div class="py-1 chatMessage">
                    <div class="avatarIcon"><v-icon>mdi-robot-outline</v-icon></div>
                    <div class="messageTextBox botMessage">
                      <div class="chatBotTyping"><span></span><span></span><span></span></div>
                    </div>
                  </div>
                </template>
                <template v-if="isWaitingForUserSelection && isFollowUpQuestion">
                  <div class="chatMessage user">
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleUserSelection(REPLY_OPTION_YES)"
                      >{{ REPLY_OPTION_YES }}</div
                    >
                  </div>
                  <div class="chatMessage user">
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleUserSelection(REPLY_OPTION_NO)"
                    >
                      {{ REPLY_OPTION_NO }}</div
                    >
                  </div>
                  <div class="chatMessage user">
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleUserSelection(REPLY_OPTION_REQUEST_HUMAN_V1)"
                      >{{ REPLY_OPTION_REQUEST_HUMAN_V1 }}</div
                    >
                  </div>
                </template>
                <template v-if="isWaitingForUserSelection && isDemoRequest">
                  <div class="chatMessage user">
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleDemoUserSelection(REPLY_OPTION_OVERVIEW)"
                      >{{ REPLY_OPTION_OVERVIEW }}</div
                    >
                  </div>
                  <div class="chatMessage user">
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleDemoUserSelection(REPLY_OPTION_FEATURE)"
                    >
                      {{ REPLY_OPTION_FEATURE }}</div
                    >
                  </div>
                  <div class="chatMessage user">
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleDemoUserSelection(REPLY_OPTION_ADVERTISING)"
                      >{{ REPLY_OPTION_ADVERTISING }}</div
                    >
                  </div>
                  <div class="chatMessage user">
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleAgentRequest()"
                      >{{ REPLY_OPTION_REQUEST_HUMAN_V2 }}</div
                    >
                  </div>
                </template>
                <template v-if="isWaitingForUserSelection && featureOptions.length">
                  <div
                    v-for="(feature, idx) in featureOptions"
                    :key="feature + idx"
                    class="chatMessage user"
                  >
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleFeatureSelection(feature)"
                      >{{ feature.title }}</div
                    >
                  </div>
                  <div class="chatMessage user">
                    <div
                      class="messageTextBox userMessage selectOption"
                      @click="handleAgentRequest()"
                      >{{ REPLY_OPTION_REQUEST_HUMAN_V2 }}</div
                    >
                  </div>
                </template>
              </template>
            </div>
          </v-card-text>
          <v-card-actions v-if="currentView === 'chat'">
            <v-textarea
              v-model="textAreaInput"
              :rows="isTextAreaFocused ? 2 : 1"
              :rules="[validationRules.maxChar, validationRules.isValidInput]"
              :placeholder="textInputPlaceholder"
              :disabled="textInputIsDisabled"
              class="chatInputTextarea"
              :counter="MAX_INPUT_CHARACTERS"
              no-resize
              solo
              flat
              @blur="isTextAreaFocused = false"
              @focus="isTextAreaFocused = true"
              @keydown="handleKeyDown"
            >
              <v-icon
                :disabled="!canSendMessage"
                class="chatInputTextareaSend"
                slot="append"
                color="#5E00EB"
                @click="handleSendAction(true)"
                >mdi-send</v-icon
              >
            </v-textarea>
          </v-card-actions>
        </v-card>
      </div>
    </Transition>
  </div>
</template>

<script>
import Parameter from 'parameter-mixin'
import WebsiteChatbotInterfaceFactory from 'bs-interface-chatbot/bs-interface-chatbot-unauthorized.js'
export default {
  name: 'AIChatbot',
  mixins: [Parameter],
  data() {
    return {
      accounts: JSON.parse(localStorage.getItem('accounts')) || {},
      currentAdvertiserId: undefined,
      currentCampaignId: undefined,
      userAccount: undefined,
      currentFEMSName: '',
      chatIsOpen: false,
      chatHistory: [],
      isTextAreaFocused: false,
      isLoadingChatHistory: false,
      // hasChatHistory - we use this flag to determine whether to show the "View Chat History" option as opposed
      // to just using the chatHistory.length to check. This is because the option to view chat history becomes
      // available after your first chat session (before the chat history array is populated)
      hasChatHistory: false,
      isViewingChatHistory: false,
      messages: [], // list of message objects
      textAreaInput: '',
      MAX_INPUT_CHARACTERS: 1200,
      validationRules: {
        maxChar: value => {
          if (!value) return true
          return (
            `${value}`.length <= this.MAX_INPUT_CHARACTERS ||
            'Question can only be 1,200 characters max.'
          )
        },
        isValidInput: value => this.validateUserInput(value),
      },
      threadId: undefined,
      visitorId: undefined,
      chatbotQuestion: '',
      chatbotQuestionTimestamp: 0,
      chatbotIsTyping: false,
      isWaitingForUserSelection: false,
      userSelection: undefined,
      currentSessionHasEnded: false,
      // the following ALL CAPS constants need to remain identical to the BE text (so verify in both before adjusting)
      // reply options
      REPLY_OPTION_YES: 'Yes, thank you!',
      REPLY_OPTION_NO: 'No, let me reword my question.',
      REPLY_OPTION_REQUEST_HUMAN_V1: `No, I'd like to speak with a support agent.`,
      REPLY_OPTION_REQUEST_HUMAN_V2: `I'd like to speak with a support agent.`,
      // asking the unauthorized user for their email address so we can get back to them
      REQUEST_USER_EMAIL_HUMAN: `Sure, please provide your email address so I can notify a support agent!`,
      isWaitingForEmailInput: false,
      // session timeout
      SESSION_TIMEOUT_SECONDS: 300, // 5 minutes
      // waiting for chatbot to respond
      CHATBOT_RESPONSE_TIMEOUT_SECONDS: 300,
      secondsSpentWaitingForChatbotResponse: 0,
      chatbotRespondedAfterTimeout: false,
      isFollowUpQuestion: false,
      isDemoRequest: false,
      // options for demo
      REPLY_OPTION_OVERVIEW: 'Platform Overview',
      REPLY_OPTION_FEATURE: 'Specific Feature',
      REPLY_OPTION_ADVERTISING: 'Teach me about advertising',
      featureOptions: [],
      // questions for demo
      DEMO_QUESTION: 'What kind of demo would you like to see?',
      FEATURE_QUESTION: 'What feature would you like to learn more about?',

      EMAIL_RESPONSE_V1: `Thank you for your email address. Someone will contact you via email as soon as possible (typically within 24 hours). If you don't hear from us soon enough, feel free to email support@adacado.com directly.`,
      EMAIL_RESPONSE_V2: `Sure, let me notify a support agent! Someone will contact you via email as soon as possible (typically within 24 hours). If you don't hear from us soon enough, feel free to email support@adacado.com directly.`,

      formDataSent: false,
      reportTitle: '',
      reportMessage: '',
      currentView: 'chat',
      feedbackPlaceholder: `We'd love to hear from you! Tell us what you like/dislike, what's working and what's not. Your feedback will help us improve our app and your experience.`,

      isSendingMessage: false,
    }
  },
  async beforeMount() {
    // fetch user data if user is logged in, else check localStorage for the visitorId
    if (this.$firebaseUser) {
      // in-application
      await this.AccountInterface.get(r => {
        if (r) this.userAccount = r
      })
      this.getAdvertiserAndCampaignIds()
    } else {
      // adacado website
      this.visitorId = localStorage.getItem('adaChatbotVisitorId')
      if (!this.visitorId) {
        this.visitorId = this.generateVisitorId()
        localStorage.setItem('adaChatbotVisitorId', this.visitorId)
      }
      // iframe is simply hidden until button is clicked, so keep the chat open
      if (!this.shouldShowChatButton) {
        this.chatIsOpen = true
      }
    }
    // get the FEMS name, only applicable for in-app pages (ie. not the adacado website)
    const pathParts = window.location.href.split('/')
    let femsNameArr = pathParts[3].split('-')
    // format the femsName from campaign-list to campaignList
    if (femsNameArr.length > 1) {
      for (let i = 0; i <= femsNameArr.length - 1; i++) {
        if (i > 0) femsNameArr[i] = femsNameArr[i][0].toUpperCase() + femsNameArr[i].slice(1)
      }
    }
    this.currentFEMSName = femsNameArr.join('')
    // initialize the message list with a greeting from the chatbot
    const firstName =
      this.userAccount?.Properties?.FirstName &&
      this.userAccount.Properties.FirstName !== 'active user'
        ? this.userAccount.Properties.FirstName
        : 'there'
    this.messages.push({
      text: `Hello ${firstName}!\nHow can I help you?`,
      isUser: false,
      messageTimestamp: Math.floor(new Date().getTime() / 1000),
    })
    await this.getChatHistory()
    this.checkLocalStorageToOpenChat()
  },
  computed: {
    windowWidth() {
      return window.innerWidth - 40
    },
    AccountInterface() {
      return window.adacado.bscs.AccountInterfaceFactory()
    },
    ChatbotInterface() {
      return window.adacado.bscs.ChatbotInterfaceFactory()
    },
    WebsiteChatbotInterface() {
      return WebsiteChatbotInterfaceFactory()
    },
    isViewingHistoricalThread() {
      if (this.chatHistory.length && this.threadId) {
        const currentThread = this.chatHistory.find(thread => thread.threadId === this.threadId)
        return (
          Math.floor(new Date().getTime() / 1000) - currentThread.endTimestamp >=
          this.SESSION_TIMEOUT_SECONDS
        )
      }
      return false
    },
    textInputPlaceholder() {
      if (this.chatbotIsTyping) {
        return 'Waiting for reply...'
      } else if (this.isWaitingForUserSelection) {
        return 'Please make a selection before typing.'
      } else if (this.isWaitingForEmailInput) {
        return 'youremail@example.com'
      }
      return 'Enter your question here'
    },
    textInputIsDisabled() {
      return this.chatbotIsTyping || this.isWaitingForUserSelection
    },
    canSendMessage() {
      // return true if there is text in the text area, and it's not too long, the text area is not disabled,
      // and if we're waiting for an email input, that the email is valid
      return (
        this.textAreaInput.length > 0 &&
        this.textAreaInput.length <= this.MAX_INPUT_CHARACTERS &&
        !this.textInputIsDisabled &&
        (!this.isWaitingForEmailInput ||
          this.validationRules.isValidInput(this.textAreaInput) == true)
      )
    },
    shouldShowChatButton() {
      // only show the chat button if the user is in the application
      return !window.location.href.includes('website-chatbot')
    },
    isMobileSized() {
      return window.innerWidth < 450
    },
  },
  watch: {
    messages: {
      deep: true,
      handler() {
        if (!this.chatIsOpen || !this.messages.length) return
        if (
          this.messages.length == 1 &&
          !this.messages[0].isUser &&
          this.messages[0].text.includes('How can I help you?')
        )
          return
        this.$nextTick(() => {
          // scroll to the bottom of the message list if the last message is from a user
          if (this.messages[this.messages.length - 1].isUser) {
            this.$refs.messageAreaContainer.scrollTop = this.$refs.messageAreaContainer.scrollHeight
          } else {
            // scroll to the top of the most recent message set, and animate a little border around the message
            let mostRecentUserMessageIndex = 0
            // find the most recent user message, and use its index + 1 to find the start of the chatbot message(s)
            const messageTextBoxes = this.$refs.messageAreaContainer.querySelectorAll(
              '.messageTextBox'
            )
            for (let i = messageTextBoxes.length - 1; i >= 0; i--) {
              if (
                messageTextBoxes[i].classList.contains('userMessage') &&
                !messageTextBoxes[i].classList.contains('selectOption')
              ) {
                mostRecentUserMessageIndex = i
                break
              }
            }
            this.$refs.messageAreaContainer.scrollTop =
              messageTextBoxes[mostRecentUserMessageIndex + 1].offsetTop - 125
            messageTextBoxes[mostRecentUserMessageIndex + 1].classList.add(
              'animateChatMessageBorder'
            )
          }
        })
      },
    },
    chatIsOpen(isOpen) {
      // scroll to the bottom of the message list
      if (isOpen) {
        this.$nextTick(() => {
          this.checkForOpenSession()
        })
      }
    },
  },
  methods: {
    sendReport() {
      this.isSendingMessage = true
      const report = {
        // question fields (only populated for incoming requests)
        report: this.textAreaInput,
        visitorId: this.visitorId,
        channel: this.currentView,
      }
      if (this.userAccount) {
        this.ChatbotInterface.sendReport(report).then(r => {
          const response = r.responseData
          if (response.status !== 200) {
            this.reportTitle =
              this.currentView === 'feedback' ? 'Feedback Send Error' : 'Bug Report Send Error'
            this.reportMessage = response.errorMessage
          } else {
            this.reportTitle =
              this.currentView === 'feedback' ? 'Feedback Received' : 'Bug Report Sent'
            this.reportMessage =
              this.currentView === 'feedback'
                ? 'Thank you for your precious feedback.'
                : 'Thank you for reporting the bug. We will look into it as soon as possible.'
          }
          this.formDataSent = true
          this.isSendingMessage = false
        })
      } else {
        this.WebsiteChatbotInterface.sendReport(report).then(r => {
          const response = r.responseData
          if (response.status !== 200) {
            this.reportTitle =
              this.currentView === 'feedback' ? 'Feedback Send Error' : 'Bug Report Send Error'
            this.reportMessage = response.errorMessage
          } else {
            this.reportTitle =
              this.currentView === 'feedback' ? 'Feedback Received' : 'Bug Report Sent'
            this.reportMessage =
              this.currentView === 'feedback'
                ? 'Thank you for your precious feedback.'
                : 'Thank you for reporting the bug. We will look into it as soon as possible.'
          }
          this.formDataSent = true
          this.isSendingMessage = false
        })
      }
      this.formDataSent = true
    },
    toggleReportForm(view) {
      this.textAreaInput = ''
      this.currentView = view
    },
    resetView() {
      this.textAreaInput = ''
      this.formDataSent = false
      this.currentView = 'chat'
    },
    validateUserInput(value) {
      // currently input is only being validated if we're waiting for an email address
      if (!this.isWaitingForEmailInput) return true
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return pattern.test(value) || 'Please enter a valid email address'
    },
    async handleSendAction(isUserQuestion = false) {
      if (this.isDemoRequest && this.isWaitingForEmailInput) {
        this.handleEmailInputFromDemoRequest()
        return
      }
      // first, add the message to the list
      const messageTimestamp = Math.floor(new Date().getTime() / 1000)
      if (isUserQuestion) {
        if (this.isViewingChatHistory) {
          this.isViewingChatHistory = false
          this.currentSessionHasEnded = true
        }
        // if the previous session has ended, start a new thread
        if (this.currentSessionHasEnded) this.startNewThread()
        this.messages.push({
          text: this.textAreaInput,
          isUser: true,
          messageTimestamp,
        })
        this.chatbotIsTyping = true
      }
      // check if user requested a demo
      if (this.textAreaInput.toLowerCase().search(/\bdemo\b/) >= 0) {
        this.chatbotIsTyping = false
        this.handleDemoRequest()
        return
      }
      // send message to chatbot
      const theMessage = {
        // question fields (only populated for incoming requests)
        questionText: isUserQuestion ? this.textAreaInput : this.chatbotQuestion,
        askedBy: isUserQuestion ? 'user' : 'chatbot',
        askedAt: isUserQuestion ? messageTimestamp : this.chatbotQuestionTimestamp,
        threadId: this.threadId,
        visitorId: this.visitorId,
      }
      if (!isUserQuestion) {
        if (this.isWaitingForEmailInput) {
          theMessage.answerText = this.textAreaInput
        } else {
          theMessage.answerText = this.userSelection
        }
        theMessage.answeredBy = 'user'
        theMessage.answeredAt = messageTimestamp
      }
      const theTextAreaInput = this.textAreaInput
      this.textAreaInput = ''
      var theRequest = undefined
      if (this.userAccount) {
        theRequest = this.ChatbotInterface.sendMessage(theMessage)
      } else {
        theRequest = this.WebsiteChatbotInterface.sendMessage(theMessage)
      }
      const startedWaiting = Math.floor(new Date().getTime() / 1000)
      await theRequest
        .then(r => {
          this.secondsSpentWaitingForChatbotResponse = 0
          if (
            !this.userAccount &&
            this.isWaitingForEmailInput &&
            this.validationRules.isValidInput(theTextAreaInput) == true
          ) {
            // only send the thank you message if the user has entered their email address
            this.messages.push({
              text: this.EMAIL_RESPONSE_V1,
              isUser: false,
              messageTimestamp,
            })
            this.isWaitingForEmailInput = false
            this.chatbotQuestion = ''
            this.chatbotQuestionTimestamp = undefined
            this.endSession()
            return
          }
          // only handle returned data if the message was a question from the user
          if (!isUserQuestion) return
          var response
          if (this.userAccount) {
            response = r.responseData
          } else {
            response = r.data
          }
          // add the chatbot's response to the message list
          this.chatbotIsTyping = false
          let replacedNavigationLink = this.replaceNavigationLinkPlaceholderIds(
            response.navigationLink
          )
          if (window.location.href.includes(replacedNavigationLink)) {
            replacedNavigationLink = ''
          }
          this.messages.push({
            text: response.answerText,
            isUser: false,
            messageTimestamp: response.answeredAt,
            navigationLink: replacedNavigationLink,
          })
          // update the threadId and visitorId
          if (!this.threadId) {
            this.threadId = response.threadId
          }
          if (!this.visitorId) {
            this.visitorId = response.visitorId
            localStorage.setItem('adaChatbotVisitorId', this.visitorId)
          }
          if (response.hasAnswer) {
            this.askFollowUp()
          }
        })
        .catch(async err => {
          console.error('Error at component-ai-chatbot:sendMessage. Error:', err)
          // our BE chatbot service only returns 513 and 510 errors, so if it's not one of those,
          // try, in a second, to get the response via the getThread call
          if (!err.message.includes('513') && !err.message.includes('510')) {
            this.secondsSpentWaitingForChatbotResponse =
              Math.floor(new Date().getTime() / 1000) - startedWaiting
            await new Promise(res => this.retryOnAPITooSlow(res))
            this.secondsSpentWaitingForChatbotResponse = 0
          }
          // remove the temporary typing indicator
          this.chatbotIsTyping = false
          if (!this.chatbotRespondedAfterTimeout) {
            this.displayChatbotUnavailableErrorMessage()
          }
        })
    },
    async handleDemoRequest() {
      // first ask if their question was answered
      this.chatbotQuestion = this.DEMO_QUESTION
      this.chatbotQuestionTimestamp = Math.floor(new Date().getTime() / 1000)
      this.messages.push({
        text: this.chatbotQuestion,
        isUser: false,
        messageTimestamp: this.chatbotQuestionTimestamp,
      })
      this.isDemoRequest = true
      this.isWaitingForUserSelection = true
      const messageTimestamp = Math.floor(new Date().getTime() / 1000)
      // send message to chatbot
      const theMessage = {
        // question fields (only populated for incoming requests)
        questionText: this.textAreaInput,
        askedBy: 'user',
        askedAt: messageTimestamp,
        threadId: this.threadId,
        visitorId: this.visitorId,
        answerText: this.chatbotQuestion,
        answeredBy: 'chatbot',
        answeredAt: this.chatbotQuestionTimestamp,
        isDemoInteraction: true,
      }
      this.textAreaInput = ''
      this.sendMessageRequest(theMessage)
    },
    async sendMessageRequest(theMessage) {
      var theRequest = undefined
      if (this.userAccount) {
        theRequest = this.ChatbotInterface.sendMessage(theMessage)
      } else {
        theRequest = this.WebsiteChatbotInterface.sendMessage(theMessage)
      }
      await theRequest
        .then(r => {
          var response
          if (this.userAccount) {
            response = r.responseData
          } else {
            response = r.data
          }
          // update the threadId and visitorId
          if (!this.threadId) {
            this.threadId = response.threadId
          }
          if (!this.visitorId) {
            this.visitorId = response.visitorId
            localStorage.setItem('adaChatbotVisitorId', this.visitorId)
          }
        })
        .catch(err => {
          // should not need retries in this scenario as we are not waiting for a BE response
          console.error('Error at component-ai-chatbot:sendMessage. Error:', err)
          this.displayChatbotUnavailableErrorMessage()
        })
    },
    handleEmailInputFromDemoRequest() {
      this.isWaitingForUserSelection = false
      const messageTimestamp = Math.floor(new Date().getTime() / 1000)
      // add the user's response to the message list
      this.messages.push({
        text: this.textAreaInput,
        isUser: true,
        messageTimestamp,
      })
      this.chatbotQuestion = this.EMAIL_RESPONSE_V1
      this.chatbotQuestionTimestamp = Math.floor(new Date().getTime() / 1000)
      this.messages.push({
        text: this.chatbotQuestion,
        isUser: false,
        messageTimestamp: this.chatbotQuestionTimestamp,
      })
      const theMessage = {
        // question fields (only populated for incoming requests)
        questionText: this.textAreaInput,
        askedBy: 'user',
        askedAt: messageTimestamp,
        threadId: this.threadId,
        visitorId: this.visitorId,
        answerText: this.chatbotQuestion,
        answeredBy: 'chatbot',
        answeredAt: this.chatbotQuestionTimestamp,
        isDemoInteraction: true,
      }
      this.textAreaInput = ''
      this.sendMessageRequest(theMessage)
      this.isWaitingForEmailInput = false
      this.chatbotQuestion = ''
      this.chatbotQuestionTimestamp = undefined
      this.isDemoRequest = false
      this.featureOptions = []
      this.endSession()
    },
    async handleAgentRequest() {
      this.isWaitingForUserSelection = false
      const messageTimestamp = Math.floor(new Date().getTime() / 1000)
      // add the user's response to the message list
      this.messages.push({
        text: this.REPLY_OPTION_REQUEST_HUMAN_V2,
        isUser: true,
        messageTimestamp,
      })
      if (!this.userAccount) {
        this.chatbotQuestion = this.REQUEST_USER_EMAIL_HUMAN
        this.isWaitingForEmailInput = true
      } else {
        this.chatbotQuestion = this.EMAIL_RESPONSE_V2
      }
      this.chatbotQuestionTimestamp = Math.floor(new Date().getTime() / 1000)
      this.messages.push({
        text: this.chatbotQuestion,
        isUser: false,
        messageTimestamp: this.chatbotQuestionTimestamp,
      })
      const theMessage = {
        // question fields (only populated for incoming requests)
        questionText: this.REPLY_OPTION_REQUEST_HUMAN_V2,
        askedBy: 'user',
        askedAt: messageTimestamp,
        threadId: this.threadId,
        visitorId: this.visitorId,
        answerText: this.chatbotQuestion,
        answeredBy: 'chatbot',
        answeredAt: this.chatbotQuestionTimestamp,
        isDemoInteraction: true,
      }
      this.sendMessageRequest(theMessage)
      if (this.userAccount) {
        this.isDemoRequest = false
        this.featureOptions = []
        this.endSession()
      }
    },
    displayChatbotUnavailableErrorMessage() {
      // add a message to the list to let the user know that the chatbot is currently unavailable
      this.messages.push({
        text: `Sorry, I'm unable to answer your question currently due to connection/service issues. Please try again later.`,
        isUser: false,
        messageTimestamp: Math.floor(new Date().getTime() / 1000),
      })
    },
    handleKeyDown(e) {
      // if the user presses enter (and isn't holding shift or control), send the message, then clear the input
      if (e.key == 'Enter' && !e.shiftKey && !e.ctrlKey) {
        e.preventDefault()
        if (this.canSendMessage) {
          // if we're waiting for the email input, this is NOT considered a user question, and is a reply,
          // so, if true, we call handleSendAction with false
          this.handleSendAction(!this.isWaitingForEmailInput)
        }
      }
    },
    // replaceNavigationLinkPlaceholderIds - replaces placeholders with ids, but will return an empty string
    // if the link includes a placeholder but the id is not available
    replaceNavigationLinkPlaceholderIds(link) {
      const containsAdvertiserIdPlaceholder = link.includes('<advertiser_id>')
      const containsCampaignIdPlaceholder = link.includes('<campaign_id>')
      // if no placeholders are present, just return the link
      if (!containsAdvertiserIdPlaceholder && !containsCampaignIdPlaceholder) return link
      // link includes placeholders, so just return an empty string if the user is not logged in
      if (!this.userAccount) return ''
      // replace the placeholders with the ids, unless they are not available, then return an empty string
      if (
        (containsAdvertiserIdPlaceholder && !this.currentAdvertiserId) ||
        (containsCampaignIdPlaceholder && !this.currentCampaignId)
      ) {
        return ''
      }
      return link
        .replace('<advertiser_id>', this.currentAdvertiserId)
        .replace('<campaign_id>', this.currentCampaignId)
    },
    getAdvertiserAndCampaignIds() {
      const userEmail = this.$firebaseUser.email || ''
      let advertiserParam = this.$getAdacadoParams(['ADVERTISERID'])
      if (!advertiserParam.error) {
        this.currentAdvertiserId = advertiserParam.ADVERTISERID
      }
      let campaignParam = this.$getAdacadoParams(['CAMPAIGNID'])
      if (!campaignParam.error) {
        this.currentCampaignId = campaignParam.CAMPAIGNID
      }
      // if either of the ids are missing, try to get them from local storage
      if (!this.currentAdvertiserId) {
        if (this.accounts[userEmail] && this.accounts[userEmail].currentAdvertiserId) {
          this.currentAdvertiserId = this.accounts[userEmail].currentAdvertiserId
        }
      }
      if (!this.currentCampaignId) {
        if (this.accounts[userEmail] && this.accounts[userEmail].currentCampaignId) {
          this.currentCampaignId = this.accounts[userEmail].currentCampaignId
        }
      }
    },
    // handleHint - will check if a hint is available and on this page, and if so, will return some html containing a clickable hint option
    handleHint(response) {
      if (!response.hasHint) return ''
      let hintIsOnThisPage = false
      // TODO - check if the hint is on this page
      if (!hintIsOnThisPage) return ''
      return `<br/><a href='${response.hintId}'>View the page hint</a>`
    },
    // askFollowUp - checks with the user to see if their question was answered
    askFollowUp() {
      // first ask if their question was answered
      this.chatbotQuestion = 'Did that answer your question?'
      this.chatbotQuestionTimestamp = Math.floor(new Date().getTime() / 1000)
      this.messages.push({
        text: this.chatbotQuestion,
        isUser: false,
        messageTimestamp: this.chatbotQuestionTimestamp,
      })
      // then add the yes/no buttons
      this.isFollowUpQuestion = true
      this.isWaitingForUserSelection = true
    },
    async handleFeatureSelection(selection) {
      this.isWaitingForUserSelection = false
      this.featureOptions = []
      const messageTimestamp = Math.floor(new Date().getTime() / 1000)
      // add the user's response to the message list
      this.messages.push({
        text: selection.title,
        isUser: true,
        messageTimestamp,
      })
      this.chatbotQuestion =
        'Here is a demo video regarding your selection: <a href="' +
        selection.link +
        '" target="_blank">' +
        selection.link +
        '</a>'
      this.chatbotQuestionTimestamp = Math.floor(new Date().getTime() / 1000)
      this.messages.push({
        text: this.chatbotQuestion,
        isUser: false,
        messageTimestamp: this.chatbotQuestionTimestamp,
      })
      // send message to chatbot
      const theMessage = {
        // question fields (only populated for incoming requests)
        questionText: selection.title,
        askedBy: 'user',
        askedAt: messageTimestamp,
        threadId: this.threadId,
        visitorId: this.visitorId,
        answerText: this.chatbotQuestion,
        answeredBy: 'chatbot',
        answeredAt: this.chatbotQuestionTimestamp,
        isDemoInteraction: true,
      }
      var theRequest = undefined
      if (this.userAccount) {
        theRequest = this.ChatbotInterface.sendMessage(theMessage)
      } else {
        theRequest = this.WebsiteChatbotInterface.sendMessage(theMessage)
      }
      await theRequest
        .then(r => {
          var response
          if (this.userAccount) {
            response = r.responseData
          } else {
            response = r.data
          }
          // update the threadId and visitorId
          if (!this.threadId) {
            this.threadId = response.threadId
          }
          if (!this.visitorId) {
            this.visitorId = response.visitorId
            localStorage.setItem('adaChatbotVisitorId', this.visitorId)
          }
          this.askFollowUp()
        })
        .catch(err => {
          // should not need retries in this scenario as we are not waiting for a BE response
          console.error('Error at component-ai-chatbot:sendMessage. Error:', err)
          this.displayChatbotUnavailableErrorMessage()
        })
    },
    async handleDemoUserSelection(selection) {
      this.isWaitingForUserSelection = false
      this.isDemoRequest = false
      const messageTimestamp = Math.floor(new Date().getTime() / 1000)
      // add the user's response to the message list
      this.messages.push({
        text: selection,
        isUser: true,
        messageTimestamp,
      })
      // send message to chatbot
      const theMessage = {
        // question fields (only populated for incoming requests)
        questionText: selection,
        askedBy: 'user',
        askedAt: messageTimestamp,
        threadId: this.threadId,
        visitorId: this.visitorId,
        isDemoOptionSelection: true,
      }
      var theRequest = undefined
      if (this.userAccount) {
        theRequest = this.ChatbotInterface.sendMessage(theMessage)
      } else {
        theRequest = this.WebsiteChatbotInterface.sendMessage(theMessage)
      }
      await theRequest
        .then(r => {
          var response
          if (this.userAccount) {
            response = r.responseData
          } else {
            response = r.data
          }
          if (selection === this.REPLY_OPTION_FEATURE) {
            this.featureOptions = response.featureOptions
            this.isWaitingForUserSelection = true
          }
          // add the chatbot's response to the message list
          this.chatbotIsTyping = false
          this.messages.push({
            text: response.answerText,
            isUser: false,
            messageTimestamp: response.answeredAt,
          })
          // update the threadId and visitorId
          if (!this.threadId) {
            this.threadId = response.threadId
          }
          if (!this.visitorId) {
            this.visitorId = response.visitorId
            localStorage.setItem('adaChatbotVisitorId', this.visitorId)
          }
          if (selection !== this.REPLY_OPTION_FEATURE) {
            this.askFollowUp()
          }
        })
        .catch(async err => {
          console.error('Error at component-ai-chatbot:sendMessage. Error:', err)
          // remove the temporary typing indicator
          this.chatbotIsTyping = false
          this.displayChatbotUnavailableErrorMessage()
        })
    },
    handleUserSelection(selection) {
      this.userSelection = selection
      this.isWaitingForUserSelection = false
      this.isFollowUpQuestion = false
      // add the user's response to the message list
      this.messages.push({
        text: selection,
        isUser: true,
        messageTimestamp: Math.floor(new Date().getTime() / 1000),
      })
      this.handleSendAction(false)
      this.userSelection = undefined
      this.addUserSelectionResponse(selection)
    },
    // addUserSelectionResponse - will handle the user's response to the follow-up question
    // by either ending the session, allowing another question to be asked, or by informing the user
    // that their question will be answered by a human via email
    addUserSelectionResponse(selection) {
      let message = {
        text: '',
        isUser: false,
        messageTimestamp: Math.floor(new Date().getTime() / 1000),
      }
      switch (selection) {
        case this.REPLY_OPTION_YES:
          message.text = `I'm happy to help! Please let me know if anything else comes up.`
          this.messages.push(message)
          this.endSession()
          break
        case this.REPLY_OPTION_NO:
          message.text = `I'm sorry about that. Please, go ahead and reword the question, or ask a new one.`
          this.messages.push(message)
          break
        case this.REPLY_OPTION_REQUEST_HUMAN_V1:
          if (!this.userAccount) {
            this.chatbotQuestion = this.REQUEST_USER_EMAIL_HUMAN
            this.chatbotQuestionTimestamp = Math.floor(new Date().getTime() / 1000)
            message.text = this.chatbotQuestion
            this.isWaitingForEmailInput = true
            this.messages.push(message)
            return // note the return here, we don't want to reset the chatbot question and timestamp like all other cases
          }
          message.text = this.EMAIL_RESPONSE_V2
          this.messages.push(message)
          this.endSession()
          break
      }
      this.chatbotQuestion = ''
      this.chatbotQuestionTimestamp = undefined
    },
    async endSession() {
      await this.getChatHistory()
      this.currentSessionHasEnded = true
      this.messages.push({
        text: `This chat session has ended. You may view previous chats by clicking the icon in the top right of this chat window. Or ask a question to begin a new session.`,
        isUser: false,
        messageTimestamp: Math.floor(new Date().getTime() / 1000),
      })
      this.hasChatHistory = true
    },
    startNewThread() {
      this.messages = []
      this.threadId = undefined
      this.currentSessionHasEnded = false
    },
    async viewChatHistory() {
      this.isViewingChatHistory = true
      this.isWaitingForUserSelection = false
      await this.getChatHistory()
    },
    async getChatHistory() {
      this.isLoadingChatHistory = true
      var theRequest = undefined
      if (this.userAccount) {
        theRequest = this.ChatbotInterface.getThreads(threads => {
          if (threads.length) {
            this.hasChatHistory = true
            this.chatHistory = threads
          }
        })
      } else {
        theRequest = this.WebsiteChatbotInterface.getThreads(
          this.visitorId ? this.visitorId : 'none'
        ).then(threads => {
          if (threads.length) {
            this.hasChatHistory = true
            this.chatHistory = threads
          }
        })
      }
      await theRequest
      this.isLoadingChatHistory = false
    },
    // getLocalAge - converts the UTC timestamp to "hours/weeks/years ago" using the local browser time
    getLocalAge(theUTCTimestampInSeconds) {
      const theCurrentUTCTimestamp = Math.floor(new Date().getTime() / 1000)
      const theDifferenceInSeconds = theCurrentUTCTimestamp - theUTCTimestampInSeconds
      const theDifferenceInMinutes = Math.floor(theDifferenceInSeconds / 60)
      const theDifferenceInHours = Math.floor(theDifferenceInMinutes / 60)
      const theDifferenceInDays = Math.floor(theDifferenceInHours / 24)
      const theDifferenceInWeeks = Math.floor(theDifferenceInDays / 7)
      const theDifferenceInYears = Math.floor(theDifferenceInDays / 365)
      if (theDifferenceInYears >= 1) {
        return `${theDifferenceInYears} year${theDifferenceInYears > 1 ? 's' : ''} ago`
      } else if (theDifferenceInWeeks >= 1) {
        return `${theDifferenceInWeeks} week${theDifferenceInWeeks > 1 ? 's' : ''} ago`
      } else if (theDifferenceInDays >= 1) {
        return `${theDifferenceInDays} day${theDifferenceInDays > 1 ? 's' : ''} ago`
      } else if (theDifferenceInHours >= 1) {
        return `${theDifferenceInHours} hour${theDifferenceInHours > 1 ? 's' : ''} ago`
      } else if (theDifferenceInMinutes >= 1) {
        return `${theDifferenceInMinutes} minute${theDifferenceInMinutes > 1 ? 's' : ''} ago`
      } else if (theDifferenceInSeconds >= 1) {
        return `${theDifferenceInSeconds} second${theDifferenceInSeconds > 1 ? 's' : ''} ago`
      } else {
        // if for whatever reason we can't calculate the age, just return an empty string
        return ''
      }
    },
    // loadSelectedThread - takes a thread id, a boolean (to determine if current chat session should be ended), and
    // another boolean (to determine if we should be checking if the session has ended - so we can ask follow up if required),
    // and loads the selected thread from the chat history
    async loadSelectedThread(threadId, threadIndex, endSession, checkingForOpenSession = false) {
      this.isWaitingForUserSelection = false
      this.threadId = threadId
      this.currentSessionHasEnded = endSession
      if (this.userAccount) {
        return this.ChatbotInterface.getThread(threadId, threadInteractions => {
          this.getThreadCallback(threadIndex, threadInteractions, checkingForOpenSession)
        })
      }
      return this.WebsiteChatbotInterface.getThread(threadId).then(threadInteractions => {
        this.getThreadCallback(threadIndex, threadInteractions, checkingForOpenSession)
      })
    },
    getThreadCallback(threadIndex, threadInteractions, checkingForOpenSession) {
      if (threadInteractions) {
        const lastInteraction = threadInteractions[threadInteractions.length - 1]
        const historicalChatSessionHasEnded = this.historicalChatSessionHasEnded(
          lastInteraction,
          threadIndex
        )
        if (checkingForOpenSession && historicalChatSessionHasEnded) {
          // when we open the chat window, we check if there is an open session, if so, we load the last chat,
          // but if the session has ended, we don't want to load the chat, so we just stop here
          return
        }
        // replace the placeholder ids in the navigation links with the actual ids, if applicable
        threadInteractions.forEach(interaction => {
          if (interaction.navigationLink) {
            interaction.navigationLink = this.replaceNavigationLinkPlaceholderIds(
              interaction.navigationLink
            )
            if (window.location.href.includes(interaction.navigationLink)) {
              interaction.navigationLink = ''
            }
          }
        })
        this.messages = threadInteractions
        if (historicalChatSessionHasEnded) {
          this.messages.push({
            text: `This chat session has ended. You may view previous chats by clicking the icon in the top right of this chat window. Or ask a question to begin a new session.`,
            isUser: false,
            messageTimestamp: lastInteraction.messageTimestamp,
          })
        } else {
          // if historical chat session has not ended
          switch (true) {
            // chatbot was in process of asking user for demo options
            case lastInteraction.text == this.DEMO_QUESTION:
              this.isWaitingForUserSelection = true
              this.isDemoRequest = true
              break
            // chatbot was in process of asking user for feature options
            case lastInteraction.text == this.FEATURE_QUESTION:
              if (this.userAccount) {
                this.ChatbotInterface.getFeatureList(list => {
                  this.featureOptions = list
                  this.isWaitingForUserSelection = true
                })
              } else {
                this.WebsiteChatbotInterface.getFeatureList(list => {
                  this.featureOptions = list
                  this.isWaitingForUserSelection = true
                })
              }
              break
            // the last message was a "not-enough-context" message, or a user message, if not, ask follow up
            case !lastInteraction.text.includes('unable to answer that question for you') &&
              !lastInteraction.isUser:
              this.askFollowUp()
              break
          }
        }
        this.isViewingChatHistory = false
      }
    },
    // checkForOpenSession - checks if the last chat is within the session timeout, if so, loads the last chat
    // chatIsOpen must be set to true before this function is called, otherwise the relevant DOM elements won't exist
    checkForOpenSession() {
      if (
        !this.chatHistory.length ||
        !this.chatIsOpen ||
        this.currentSessionHasEnded ||
        Math.floor(new Date().getTime() / 1000) - this.chatHistory[0].endTimestamp >=
          this.SESSION_TIMEOUT_SECONDS
      )
        return
      this.loadSelectedThread(this.chatHistory[0].threadId, 0, false, true)
    },
    // historicalChatSessionHasEnded - returns true if the message is from an expired session OR if the message is
    // from the user AND contains one of the following follow-up question's reply options (REPLY_OPTION_YES, REPLY_OPTION_REQUEST_HUMAN_V1, REPLY_OPTION_REQUEST_HUMAN_V2)
    historicalChatSessionHasEnded(interaction, threadIndex) {
      // if this thread is not the most recent thread, then consider it expired
      if (threadIndex != 0) return true
      // if this thread is the most recent thread, but the last interaction was less recent than the timeout, then consider it expired
      if (
        Math.floor(new Date().getTime() / 1000) - interaction.timestampUTC >=
        this.SESSION_TIMEOUT_SECONDS
      ) {
        return true
      }
      if (
        interaction.text == this.EMAIL_RESPONSE_V1 ||
        interaction.text == this.EMAIL_RESPONSE_V2
      ) {
        return true
      }
      return (
        interaction.isUser &&
        (interaction.text == this.REPLY_OPTION_YES ||
          interaction.text == this.REPLY_OPTION_REQUEST_HUMAN_V1 ||
          interaction.text == this.REPLY_OPTION_REQUEST_HUMAN_V2)
      )
    },
    // checkLocalStorageToOpenChat - if the user was redirected to a page by the chatbot, it will automatically open the chat window
    checkLocalStorageToOpenChat() {
      if (localStorage.getItem('wasRedirectedByChatbot')) {
        this.chatIsOpen = true
        localStorage.removeItem('wasRedirectedByChatbot')
      }
    },
    setLocalStorageAndRedirect(link) {
      localStorage.setItem('wasRedirectedByChatbot', 'true')
      if (this.userAccount) {
        window.location.href = link
        return
      }
      window.open(link, '_blank')
    },
    async retryOnAPITooSlow(res) {
      // wait 3 seconds before each retry
      await new Promise(r => setTimeout(r, 3000))
      this.secondsSpentWaitingForChatbotResponse += 3
      // only wait for 300 seconds at most
      if (this.secondsSpentWaitingForChatbotResponse >= this.CHATBOT_RESPONSE_TIMEOUT_SECONDS) {
        this.chatbotRespondedAfterTimeout = false
        res()
        return
      }
      if (this.userAccount) {
        // in-application
        this.ChatbotInterface.getThreads(threads => {
          if (!threads.length) return this.retryOnAPITooSlow(res)
          this.ChatbotInterface.getThread(threads[0].threadId, threadInteractions => {
            this.checkChatbotHasFinallyReplied(threadInteractions, threads[0].threadId, res)
          })
        }).catch(err => {
          console.error('Error at component-ai-chatbot:retryOnAPITooSlow. Error:', err)
          this.retryOnAPITooSlow()
        })
        return
      }
      // adacado website
      this.WebsiteChatbotInterface.getThreads(this.visitorId)
        .then(threads => {
          if (!threads.length) return this.retryOnAPITooSlow(res)
          this.WebsiteChatbotInterface.getThread(threads[0].threadId).then(threadInteractions => {
            this.checkChatbotHasFinallyReplied(threadInteractions, threads[0].threadId, res)
          })
        })
        .catch(err => {
          console.error('Error at component-ai-chatbot:retryOnAPITooSlow. Error:', err)
          this.retryOnAPITooSlow(res)
        })
    },
    generateVisitorId() {
      let result = ''
      const alphaNumericOptions = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      for (let i = 0; i < 20; i++) {
        result += alphaNumericOptions.charAt(Math.floor(Math.random() * alphaNumericOptions.length))
      }
      return btoa(result + '-' + Math.floor(new Date().getTime() / 1000))
        .replaceAll('/', '_')
        .replaceAll('+', '-')
    },
    checkChatbotHasFinallyReplied(threadInteractions, threadId, res) {
      if (!threadInteractions || threadInteractions.length < 2) return this.retryOnAPITooSlow(res)
      // if the last interaction is the chatbot, and the second last interaction is the user, and the
      // second last interaction's text matches the last message's text, then load the thread
      const latestInteraction = threadInteractions[threadInteractions.length - 1]
      const secondLastInteraction = threadInteractions[threadInteractions.length - 2]
      if (
        !latestInteraction.isUser &&
        secondLastInteraction?.isUser &&
        secondLastInteraction.text == this.messages[this.messages.length - 1].text
      ) {
        this.loadSelectedThread(threadId, 0, false, true)
        this.chatbotRespondedAfterTimeout = true
        res()
        return
      }
      this.retryOnAPITooSlow(res)
    },
  },
}
</script>

<style lang="scss" scoped>
* {
  font-family: 'Roboto', sans-serif;
}
.chatButton {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 999;
}
.chatContainer {
  position: fixed;
  bottom: 100px;
  right: 20px;
  height: 700px;
  z-index: 999;
  .chatMessage {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
  .chatMessage.user {
    justify-content: flex-end;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.chatIcon {
  transform: scaleX(-1); // flip the chat icon horizontally
}
.adacadoLogo {
  width: 100px;
}
.logoAndHistory {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  background-color: #ff9f21;
}
.iconsAndHistoryText {
  display: flex;
  justify-content: center;
  align-items: center;
}
.messageAreaContainer {
  height: 76%;
  overflow-y: auto;
  border: 10px solid #5e00eb;
  background-color: white;
  border-radius: 14px;
  &.textAreaFocused {
    height: 73%;
  }
}
.optionsText {
  position: absolute;
  right: 25px;
  top: 65px;
}
.formContainer {
  overflow-y: auto;
  border: 10px solid #5e00eb;
  border-radius: 14px !important;
  background-color: #5e00eb;
  .sendReportButton {
    position: absolute;
    right: 20px;
    cursor: pointer;
    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }
}
.reportTitle {
  font-size: 16px;
}
.messageTextBox {
  margin: 4px;
}
.botMessage {
  background-color: #e4e4e4;
  border-radius: 20px;
  padding: 15px;
  width: fit-content;
}
.userMessage {
  background-color: #5e00eb;
  color: white;
  border-radius: 20px;
  padding: 15px;
  width: fit-content;
}
.userMessage.selectOption {
  background-color: #5e00eba8;
  cursor: pointer;
  padding: 6px;
  border-radius: 5px;
}
.userMessage.selectOption:hover {
  background-color: #5e00eb48;
  color: black;
}
.chatInputTextareaSend {
  cursor: pointer;
}
.chatHistoryButtons {
  cursor: pointer;
  color: #5e00eb;
}
.loadingSpinnerContainer {
  display: flex;
  justify-content: center;
}
.threadPreview {
  cursor: pointer;
}
.threadPreviewRow:hover {
  background-color: #5e00eb2d;
}
.whiteSpace {
  white-space: pre-line;
}
/* animated chatbot typing/loading indicator */
.chatBotTyping {
  text-align: center;
}
.chatBotTyping span {
  display: inline-block;
  vertical-align: middle;
  width: 8px;
  height: 8px;
  background: #5e00eb;
  border-radius: 8px;
  animation: chatBotTyping 0.8s infinite alternate;
}
.chatBotTyping span:nth-of-type(2) {
  animation-delay: 0.2s;
}
.chatBotTyping span:nth-of-type(3) {
  animation-delay: 0.6s;
}
@media screen and (max-height: 847px) {
  .chatContainer {
    height: 600px;
  }
  .chatContainer .rounded-lg.v-card.v-sheet.theme--light {
    height: 600px !important;
  }
  .chatContainer .messageAreaContainer {
    height: 72%;
    &.textAreaFocused {
      height: 68%;
    }
  }
}
@media screen and (max-height: 751px) {
  .chatContainer {
    height: 515px;
  }
  .chatContainer .rounded-lg.v-card.v-sheet.theme--light {
    height: 515px !important;
  }
  .chatContainer .messageAreaContainer {
    height: 68%;
    &.textAreaFocused {
      height: 63%;
    }
  }
}
@keyframes chatBotTyping {
  0% {
    opacity: 0.9;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.1;
    transform: scale(1);
  }
}
/* animated chat message border */
.animateChatMessageBorder {
  animation: backgroundChange 0.75s normal;
}
@keyframes backgroundChange {
  from {
    background-color: #5e00eb8a;
  }
  to {
    background-color: #e4e4e4;
  }
}
/* vuetify will override these styles without ::v-deep */
::v-deep .chatInputTextarea .v-text-field__details {
  margin-top: -5px !important;
  z-index: 2 !important;
}
::v-deep .chatInputTextarea .v-text-field__details .theme--light.v-counter {
  color: white !important;
}
::v-deep .chatInputTextarea .v-text-field__details .error--text {
  font-weight: bold !important;
  color: #ff9f21 !important;
  caret-color: #ff9f21 !important;
}
a {
  color: #1976d2;
  cursor: pointer;
}
</style>
